import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useOktaAuth } from '@okta/okta-react';
import { sessionManager } from '../utils/sessionManager';
import { getUserDetails } from '../services/userService';
import appInsightsLogger from '../utils/appInsights.js';
import { CONFIG } from '../config/config.js';

// Create AuthContext
const AuthContext = createContext(null);

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const { oktaAuth, authState } = useOktaAuth();
  console.log('=== AuthProvider render ===', { authState, isAuthenticated: authState?.isAuthenticated, isPending: authState?.isPending });
  
  const [user, setUser] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState(null);
  const [isExternal, setIsExternal] = useState(null);
  const isInitializingRef = React.useRef(false); // Prevent multiple simultaneous inits

  // Derive isLoggedIn from Okta's authState
  const isLoggedIn = authState?.isAuthenticated ?? false;

  // Logout function - defined early so it can be reused in initializeAuth
  const logout = useCallback(async (isTimeout = false) => {
    console.log('=== Logout started ===', { isTimeout });
    // Show a blocking overlay while logging out to avoid route flicker
    setIsInitializing(true);
    
    // Track logout event
    appInsightsLogger.trackEvent('User_Logout', {
      component: 'AuthContext',
      reason: isTimeout ? 'session_timeout' : 'manual_logout'
    });

    try {
      // Clear local session data
      sessionManager.clearSession();
      appInsightsLogger.clearUser();
      
      // Update context state
      setUser(null);
      setError(null);
      setIsExternal(null);
      
      // Sign out from Okta (clears tokens and redirects)
      console.log('Signing out from Okta...');
      await oktaAuth.signOut({ 
        postLogoutRedirectUri: CONFIG.OKTA_POST_LOGOUT_REDIRECT_URI,
        revokeAccessToken: true,
        revokeRefreshToken: true
      });
      
      console.log('=== Logout successful ===');
    } catch (error) {
      console.error('Logout error:', error);
      appInsightsLogger.trackException(error, {
        component: 'AuthContext',
        operation: 'logout'
      });

      // Fallback: Clear tokens manually and force redirect
      try {
        await oktaAuth.tokenManager.clear();
      } catch (clearError) {
        console.error('Failed to clear tokens:', clearError);
      }
      
      // Force redirect to login
      window.location.href = CONFIG.OKTA_POST_LOGOUT_REDIRECT_URI;
    }
  }, [oktaAuth]);

  // Initialize authentication state and load backend user profile
  const initializeAuth = useCallback(async () => {
    isInitializingRef.current = true;
    console.log('=== AuthContext: initializeAuth START ===');
    console.log('AuthContext: Current authState:', authState);
    // Ensure UI waits until backend user profile is fully loaded
    setIsInitializing(true);
    
    try {
        console.log('AuthContext: User is authenticated, fetching user claims...');
        
        let claims;
        try {
          // Get ID token/user claims from Okta SDK (for email only)
          claims = await oktaAuth.getUser();
          console.log('Okta authentication detected, claims:', claims);
        } catch (claimsErr) {
          console.error('AuthContext: Failed to get Okta user claims:', claimsErr);
          setError('Failed to retrieve user information from Okta');
          // Reuse logout method - handles all cleanup including setIsInitializing
          await logout();
          return;
        }

        // Extract only username and email from Okta - everything else comes from backend
        const username = claims.preferred_username;
        const email = claims.email;
        
        if (!username) {
          console.error('AuthContext: No username found in Okta claims');
          setError('No username found in user profile');
          // Reuse logout method - handles all cleanup including setIsInitializing
          await logout();
          return;
        }
        
        const oktaFirstName = claims.given_name || (claims.name ? claims.name.split(' ')[0] : '') || '';
        const oktaLastName = claims.family_name || (claims.name ? claims.name.split(' ').slice(1).join(' ') : '') || '';

        console.log('AuthContext: Fetching user details from backend for username:', username);
        
        try {
          // Fetch user details from backend - THIS IS REQUIRED
          const res = await getUserDetails(username);
          const userData = res.data || res;
          console.log('AuthContext: Backend user details received:', userData);

          // Get userId and isExternal from BACKEND, not Okta
          const userId = userData.userId;
          const userIsExternal = userData.isExternal ?? true;
          const role = userIsExternal ? 'client' : 'admin';

          // Start session with backend data
          const sessionData = sessionManager.startSession({ userId, email, username, isExternal: userIsExternal });
          sessionManager.resetSessionTimer();

          // Set complete user state with all data in one object
          setUser({ 
            // Auth essentials
            emailId: userData.emailId || email,
            userId, 
            sessionId: sessionData.sessionId, 
            role,
            isExternal: userIsExternal,
            username: userData.userName || username,
            
            // Profile details
            firstName: userData.firstName || oktaFirstName,
            lastName: userData.lastName || oktaLastName,
            organization: userData.organization || '',
            contact: userData.contact || '',
            organizationId: userData.organizationId || null,
            lastLoginDate: userData.lastLoginDate || null,
            passwordModifiedDate: userData.passwordModifiedDate || null
          });
          setIsExternal(userIsExternal);

          // NOW we can stop the loader - backend data is loaded
          console.log('AuthContext: Backend data loaded, setting isInitializing to false');
          setIsInitializing(false);

          appInsightsLogger.trackEvent('Auth_Okta_Session_Success', {
            component: 'AuthContext', 
            operation: 'initializeAuth', 
            userId, 
            email, 
            isExternal: userIsExternal
          });

        } catch (fetchErr) {
          const errorMessage = fetchErr.response?.data?.message || fetchErr.message || '';
          const isUserNotFound = errorMessage.toLowerCase().includes('user not found');
          
          appInsightsLogger.trackException(fetchErr, {
            component: 'AuthContext', 
            operation: 'initializeAuth', 
            errorType: isUserNotFound ? 'user_not_found' : 'user_details_fetch_failed',
            status: fetchErr.response?.status,
            email
          });
          
          if (isUserNotFound) {
            // User authenticated with Okta but not authorized in our system
            console.log('AuthContext: User not found in backend database');
            setError('Unauthorized user - Your account is not registered in the system.');
            setIsInitializing(false);
            // Don't logout - let them see the error message
          } else {
            // Other errors (network, server down, etc) - logout for safety
            setError(`Failed to load user profile: ${errorMessage}`);
            console.log('AuthContext: Signing out user due to backend fetch failure');
            await logout();
          }
        }

      console.log('=== AuthContext: initializeAuth END ===');
    } catch (error) {
      // Unexpected error during initialization
      console.error('AuthContext: initializeAuth error:', error);
      appInsightsLogger.trackException(error, {
        component: 'AuthContext', operation: 'initializeAuth', errorType: error.name || 'UnknownError'
      });
      setError('Failed to initialize authentication');
      await logout();
    } finally {
      isInitializingRef.current = false;
      console.log('AuthContext: initializeAuth complete, ref cleared');
    }
  }, [oktaAuth, authState, logout]); // Added logout to dependencies

  // Initialize authentication state when authState changes
  useEffect(() => {
    console.log('=== AuthContext: authState changed ===', {
      isAuthenticated: authState?.isAuthenticated,
      isPending: authState?.isPending,
      hasAuthState: !!authState,
      authState
    });
    
    if (!authState) {
      // Still loading authState from Okta
      console.log('AuthContext: No authState yet, waiting for Okta to initialize...');
      setIsInitializing(true);
      
      // Fallback: If authState is still null after 5 seconds, try to update it manually
      const timeout = setTimeout(async () => {
        console.warn('AuthContext: authState still null after 5s, attempting manual update...');
        try {
          await oktaAuth.authStateManager.updateAuthState();
        } catch (err) {
          setError('Failed to initialize authentication. Please refresh the page or try again later.');
          appInsightsLogger.trackException(err, {
            component: 'AuthContext',
            operation: 'updateAuthState_fallback'
          });
          setIsInitializing(false);
        }
      }, 5000);
      
      return () => clearTimeout(timeout);
    }

    if (authState.isPending) {
      console.log('AuthContext: authState is pending, waiting...');
      setIsInitializing(true);
      return;
    }

    // Only call initializeAuth if user is authenticated
    if (authState.isAuthenticated) {
      console.log('AuthContext: User authenticated, calling initializeAuth');
      initializeAuth();
    } else {
      // User is not authenticated - clear state and allow router to redirect
      console.log('AuthContext: User not authenticated, clearing state');
      setUser(null);
      setIsExternal(null);
      sessionManager.clearSession();
      setIsInitializing(false); // Allow router to redirect to login
    }
  }, [authState, initializeAuth, oktaAuth]);

  const updateUser = useCallback((updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Context value - memoized to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    // State
    isLoggedIn,
    user,
    isInitializing,
    error,
    isExternal,

    // Actions
    logout,
    updateUser,
    clearError,
    setIsExternal,

    // Computed values
    userRole: user?.role || '',
    userName: user?.firstName && user?.lastName 
      ? `${user.firstName} ${user.lastName}` 
      : user?.emailId || ''
  }), [isLoggedIn, user, isInitializing, error, isExternal, logout, updateUser, clearError]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AuthContext;
